import type { CSSProperties } from 'react';
import type {
  BlockComponentStateId,
  BlockComponentType,
  ComputedVisibilityPayloadType,
  VisibilityPayloadType,
} from '../../types/block';
import type { ButtonColors, ButtonSizes, ButtonVariants } from '../../types/blocks/button';
import type { ValueOrCondition } from '../../types/conditionalValueFilters';
import type {
  ComputedConditionEvaluationObject,
  ConditionEvaluationObject,
} from '../../types/conditionEvaluationObject';
import type { SlotType } from '../../types/slot';
import type { StyleObject } from '../../types/styleObject';
import type { BlockTooltipAddOn } from '../common/tooltipTypes';

export type ReCaptchaContent = {
  reCaptchaSiteKey?: string;
  reCaptchaVersion?: 'reCaptchaV3' | 'reCaptchaV2';
};

export type ButtonTooltipContent = {
  content?: string;
};

export type Content = {
  /**
   * Value text for the Button
   */
  value: string;
  /**
   *
   * this field should not appear in properties panel
   * until support for submit and reset type is added
   *
   * The type of the button
   */
  type?: 'default' | 'submit' | 'reset';
  /**
   * text: renders content.value as label (default)
   * custom: renders the content slot node as label
   */
  contentMode?: 'text' | 'custom';
  /**
   * Id of the form to which the button belongs
   */
  formBlockId?: string;

  addOns?: {
    /**
     * recaptcha config for the button, verify the user with recaptcha before calling the onClick
     */
    reCaptcha?: ReCaptchaContent;
    /**
     * Tooltip add-on shown on hover. Supports a plain-text label or a custom slot.
     */
    tooltip?: BlockTooltipAddOn;
    /**
     * @deprecated legacy text-only tooltip. New writes go to `tooltip`; kept for back-compat reads.
     */
    buttonTooltip?: ButtonTooltipContent;
  };
};

export type ButtonAppearance = {
  color?: ButtonColors;
  size?: ButtonSizes;
  variant?: ButtonVariants;
  /**
   * Icon for decorating the start of the Button
   */
  startDecorator?: ValueOrCondition<string>;
  /**
   * Icon for decorating the end of the Button
   */
  endDecorator?: ValueOrCondition<string>;
  /**
   * If true, the button will be disabled
   */
  disabled?: ConditionEvaluationObject;
  /**
   * If true, the button will be in loading state
   */
  loading?: boolean;
  styles?: Pick<
    StyleObject,
    | 'borderStyle'
    | 'color'
    | 'padding'
    | 'margin'
    | 'gap'
    | 'fontSize'
    | 'width'
    | 'maxWidth'
    | 'minWidth'
  >;
  className?: string;
  visibility?: VisibilityPayloadType;
};

export type ComputedButtonAppearance = Omit<ButtonAppearance, 'disabled'> & {
  disabled?: ComputedConditionEvaluationObject;
  visibility?: ComputedVisibilityPayloadType;
};

export type ButtonSlots = {
  content?: SlotType;
  /**
   *  Slot rendered inside the tooltip when tooltip type is 'slot'
   */
  tooltip?: SlotType;
};

export type ButtonComponentType = BlockComponentType<
  'Button',
  Content,
  ButtonAppearance,
  ButtonSlots
>;

export type ButtonBlockStateType = Pick<
  ButtonComponentType,
  'appearance' | 'content' | 'componentType'
> & {
  /**
   * is populated when reCaptcha is enabled and the user has successfully completed the reCaptcha
   */
  reCaptchaToken?: string | null;
} & BlockComponentStateId;

export type ComputedButtonBlockStateType = Omit<ButtonBlockStateType, 'appearance'> & {
  appearance?: ComputedButtonAppearance;
};

export enum ButtonCustomStylesKeys {
  Text = 'text',
  StartIcon = 'startIcon',
  EndIcon = 'endIcon',
}

export type ButtonCustomStyles = CSSProperties & {
  [ButtonCustomStylesKeys.Text]: CSSProperties;
  [ButtonCustomStylesKeys.StartIcon]: CSSProperties;
  [ButtonCustomStylesKeys.EndIcon]: CSSProperties;
};
